import { useState, useMemo } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { inventoryService } from "../services/database";
import { useAuth } from "../contexts/AuthContext";
import type { InventoryItem, Asset } from "../types/database";
import Modal from "../components/Modal";
import AddInventoryItemForm from "../components/AddInventoryItemForm";

interface InventoryItemWithAssets extends InventoryItem {
  assets?: Asset[];
}

type SortField = "name" | "category" | "qty" | "available" | "out";
type SortDirection = "asc" | "desc";

export default function Inventory() {
  const navigate = useNavigate();
  const { orgId } = useParams();
  const { currentOrganization } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [view, setView] = useState<"grid" | "list">("list");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [sortField, setSortField] = useState<SortField>("name");
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");

  // Calculate item stats
  const getItemStats = (item: InventoryItemWithAssets) => {
    // In a real implementation, these would be calculated from the assets
    // For now, we'll use placeholder values
    const assets = item.assets || [];
    const qty = assets.length;
    const available = assets.filter(
      (asset: Asset) => asset.status === "available"
    ).length;
    const out = assets.filter(
      (asset: Asset) => asset.status === "checked_out"
    ).length;

    return { qty, available, out };
  };

  // Fetch inventory items
  const {
    data: items = [],
    isLoading,
    error,
  } = useQuery<InventoryItemWithAssets[]>({
    queryKey: ["inventory", currentOrganization?.id],
    queryFn: () => inventoryService.getInventoryItems(currentOrganization?.id),
  });

  // Get unique categories
  const categories = useMemo(() => {
    const uniqueCategories = new Set(items.map((item) => item.category));
    return ["all", ...Array.from(uniqueCategories)];
  }, [items]);

  // Filter items based on search and category
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      const matchesSearch =
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.category.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesCategory =
        categoryFilter === "all" || item.category === categoryFilter;
      return matchesSearch && matchesCategory;
    });
  }, [items, searchTerm, categoryFilter]);

  // Calculate item stats for all items
  const itemsWithStats = useMemo(() => {
    return filteredItems.map((item) => {
      const stats = getItemStats(item);
      return { ...item, ...stats };
    });
  }, [filteredItems]);

  // Sort items based on sortField and sortDirection
  const sortedItems = useMemo(() => {
    return [...itemsWithStats].sort((a, b) => {
      let aValue: string | number = a[sortField];
      let bValue: string | number = b[sortField];

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
  }, [itemsWithStats, sortField, sortDirection]);

  // Handle sort click
  const handleSortClick = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  // Render sort indicator
  const renderSortIndicator = (field: SortField) => {
    if (sortField !== field) return null;

    return (
      <span className="ml-1 inline-block">
        {sortDirection === "asc" ? "▲" : "▼"}
      </span>
    );
  };

  // Handle add item success
  const handleAddItemSuccess = (itemId: string) => {
    setIsAddModalOpen(false);
    navigate(`/org/${orgId}/inventory/${itemId}`);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading inventory...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-red-500">
          Error loading inventory. Please try again later.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Inventory</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage your equipment inventory
          </p>
        </div>
        <button
          className="px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors"
          onClick={() => setIsAddModalOpen(true)}
        >
          Add Item
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder="Search inventory..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-4">
          <select
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            {categories.map((category) => (
              <option key={category} value={category}>
                {category.charAt(0).toUpperCase() + category.slice(1)}
              </option>
            ))}
          </select>
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              className={`px-3 py-2 ${
                view === "grid"
                  ? "bg-primary text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => setView("grid")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z"
                />
              </svg>
            </button>
            <button
              className={`px-3 py-2 ${
                view === "list"
                  ? "bg-primary text-white"
                  : "bg-white text-gray-600 hover:bg-gray-50"
              }`}
              onClick={() => setView("list")}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 10h16M4 14h16M4 18h16"
                />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Results count */}
      <div className="text-sm text-gray-500">
        Showing {sortedItems.length} items
      </div>

      {/* Grid View */}
      {view === "grid" ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          {sortedItems.map((item) => {
            const { qty, available, out } = item;

            return (
              <Link
                key={item.id}
                to={`/org/${orgId}/inventory/${item.id}`}
                className="bg-white rounded-lg shadow hover:shadow-lg transition-shadow border border-gray-100 overflow-hidden flex flex-col"
              >
                <div className="aspect-video relative">
                  <img
                    src={
                      item.images[0] ||
                      "https://placehold.co/600x400?text=NO+IMAGE"
                    }
                    alt={item.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-2 right-2">
                    <span className="px-2.5 py-1 bg-black/60 text-white text-xs font-medium rounded-full">
                      {item.category}
                    </span>
                  </div>
                </div>
                <div className="p-5">
                  <h3 className="font-semibold text-gray-900 text-lg mb-3">
                    {item.name}
                  </h3>
                  <div className="pt-3 border-t border-gray-100">
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-gray-600">Quantity</span>
                      <span className="font-medium">{qty}</span>
                    </div>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-gray-600">Available</span>
                      <span className="font-medium text-green-600">
                        {available}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-600">Out</span>
                      <span className="font-medium text-primary">{out}</span>
                    </div>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left bg-gray-50">
                <th className="p-4">Image</th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("name")}
                >
                  Name {renderSortIndicator("name")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("category")}
                >
                  Category {renderSortIndicator("category")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("qty")}
                >
                  Qty {renderSortIndicator("qty")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("available")}
                >
                  Available {renderSortIndicator("available")}
                </th>
                <th
                  className="p-4 cursor-pointer hover:bg-gray-100"
                  onClick={() => handleSortClick("out")}
                >
                  Out {renderSortIndicator("out")}
                </th>
                <th className="p-4">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((item) => {
                const { qty, available, out } = item;
                const detailUrl = `/org/${orgId}/inventory/${item.id}`;

                return (
                  <tr
                    key={item.id}
                    className="border-t hover:bg-gray-50 transition-colors cursor-pointer"
                    onClick={() => navigate(detailUrl)}
                    onDoubleClick={() => navigate(detailUrl)}
                  >
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <img
                        src={
                          item.images[0] ||
                          "https://placehold.co/600x400?text=NO+IMAGE"
                        }
                        alt={item.name}
                        className="h-12 w-12 object-cover rounded-lg"
                      />
                    </td>
                    <td className="p-4 font-medium">{item.name}</td>
                    <td className="p-4">{item.category}</td>
                    <td className="p-4">{qty}</td>
                    <td className="p-4">{available}</td>
                    <td className="p-4">
                      <span
                        className={`${
                          out > 0 ? "text-primary" : "text-gray-500"
                        }`}
                      >
                        {out}
                      </span>
                    </td>
                    <td className="p-4" onClick={(e) => e.stopPropagation()}>
                      <Link
                        to={detailUrl}
                        className="text-primary hover:underline"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Add Item Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add Inventory Item"
        size="lg"
      >
        <AddInventoryItemForm
          onSuccess={handleAddItemSuccess}
          onCancel={() => setIsAddModalOpen(false)}
        />
      </Modal>
    </div>
  );
}
